import { Activity, CheckCircle2, Clock3, Gauge, RefreshCw, TriangleAlert, Users } from "lucide-react";
import { api } from "../lib/api";
import { displayError, formatDuration, formatNumber, truncate } from "../lib/format";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "../components/Ui";

export default function DashboardPage() {
  const { data, loading, error, refresh } = useResource((signal) => api.overview(signal), [], 10_000);

  const capacity = Number(data?.accounts?.active_capacity ?? 0);
  const configuredCapacity = Number(data?.accounts?.configured_capacity ?? capacity);
  const browserCapacity = Number(data?.accounts?.browser_capacity ?? capacity);
  const occupied = Number(data?.accounts?.occupied_capacity ?? data?.tasks?.in_progress ?? 0);
  const usage = capacity > 0 ? Math.min(100, Math.round((occupied / capacity) * 100)) : occupied > 0 ? 100 : 0;
  const stats = [
    { label: "可用账号", value: data?.accounts?.active, note: `共 ${formatNumber(data?.accounts?.total)} 个`, icon: Users, tone: "green" },
    { label: "上游在途", value: data?.tasks?.in_progress, note: `${usage}% 槽位占用`, icon: Activity, tone: "blue" },
    { label: "等待处理", value: data?.tasks?.queued, note: "含准备与重试", icon: Clock3, tone: "amber" },
    { label: "近一小时完成", value: data?.throughput?.completed_last_hour, note: `均时 ${formatDuration(data?.throughput?.avg_duration_seconds)}`, icon: CheckCircle2, tone: "green" },
    { label: "近一小时失败", value: data?.throughput?.failed_last_hour, note: `累计 ${formatNumber(data?.tasks?.failed)}`, icon: TriangleAlert, tone: "red" },
  ];

  return (
    <div className="page-stack">
      <PageHeader
        title="运行概览"
        description="队列、账号容量与 worker 的当前状态"
        actions={<button className="button button-secondary" onClick={() => void refresh()}><RefreshCw size={16} />刷新</button>}
      />
      {loading && !data ? <LoadingState /> : error && !data ? <ErrorState error={error} onRetry={refresh} /> : (
        <>
          <section className="stat-grid" aria-label="关键指标">
            {stats.map(({ label, value, note, icon: Icon, tone }) => (
              <article className="stat-card" key={label}>
                <div className={`stat-icon stat-icon-${tone}`}><Icon size={18} /></div>
                <span>{label}</span>
                <strong>{formatNumber(value)}</strong>
                <small>{note}</small>
              </article>
            ))}
          </section>

          <div className="dashboard-grid">
            <section className="panel capacity-panel">
              <div className="panel-heading">
                <div><h3>可调度容量</h3><p>受全局上限与在线浏览器会话共同约束</p></div>
                <Gauge size={19} />
              </div>
              <div className="capacity-value"><strong>{formatNumber(occupied)}</strong><span>/ {formatNumber(capacity)}</span></div>
              <div className="progress-track"><span style={{ width: `${usage}%` }} /></div>
              <div className="capacity-legend">
                <span><i className="legend-dot legend-used" />占用 {formatNumber(occupied)}</span>
                <span><i className="legend-dot legend-free" />空闲 {formatNumber(Math.max(0, capacity - occupied))}</span>
                <span><i className="legend-dot legend-configured" />配置 {formatNumber(configuredCapacity)}</span>
                <span><i className="legend-dot legend-browser" />浏览器 {formatNumber(browserCapacity)}</span>
                <span><i className="legend-dot legend-risk" />需重登 {formatNumber(data?.accounts?.reauth_required)}</span>
              </div>
            </section>

            <section className="panel task-distribution">
              <div className="panel-heading"><div><h3>任务分布</h3><p>持久化状态机实时计数</p></div></div>
              <div className="distribution-list">
                {Object.entries(data?.tasks?.raw || {}).length === 0 ? <EmptyState label="暂无任务" /> : Object.entries(data?.tasks?.raw || {}).map(([status, count]) => (
                  <div className="distribution-row" key={status}>
                    <StatusBadge value={status} />
                    <span className="distribution-line"><i style={{ width: `${Math.max(2, Math.min(100, Number(count) / Math.max(1, Number(data?.tasks?.total)) * 100))}%` }} /></span>
                    <strong>{formatNumber(count)}</strong>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="panel">
            <div className="panel-heading"><div><h3>近期失败</h3><p>优先处理未知远端状态与认证问题</p></div></div>
            {!data?.recent_failures?.length ? <EmptyState label="近期没有失败任务" /> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>任务</th><th>状态</th><th>模型</th><th>账号</th><th>错误</th></tr></thead>
                  <tbody>{data.recent_failures.map((task) => (
                    <tr key={task.id}>
                      <td><code>{truncate(task.id, 18)}</code></td>
                      <td><StatusBadge value={task.status} /></td>
                      <td>{task.model || "-"}</td>
                      <td>{task.account_id || "-"}</td>
                      <td className="cell-error" title={displayError(task.error)}>{truncate(displayError(task.error), 80)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
